import { Button } from "@/components/ui/button";
import { Zap, TrendingUp, AlertTriangle } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { DD } from "./constants";
import type { CompetitorPressure } from "./types";

export function EmptyState({
  locationId,
  onGenerateMonth,
  isGenerating,
}: {
  locationId: string;
  onGenerateMonth: () => void;
  isGenerating: boolean;
}) {
  const { data: pressure } = useQuery<CompetitorPressure>({
    queryKey: ["/api/competitor-pressure", locationId],
    enabled: !!locationId,
  });

  const competitorCount = pressure?.competitors?.length || 0;
  const avgCompPosts = pressure?.avgCompetitorPosts || 0;

  return (
    <div className="flex flex-col items-center justify-center py-16 px-4" data-testid="empty-state">
      <div
        className="w-full max-w-2xl rounded-2xl p-8 text-center space-y-6"
        style={{
          background: "linear-gradient(135deg, rgba(249,115,22,0.12), rgba(245,158,11,0.06))",
          border: "1px solid rgba(249,115,22,0.25)",
        }}
      >
        <div className="flex items-center justify-center gap-2 text-amber-400">
          <AlertTriangle className="w-5 h-5" />
          <span className="text-sm font-semibold uppercase tracking-wider" data-testid="text-competitor-alert">
            Competitor Activity Detected
          </span>
        </div>

        <div className="space-y-2">
          <h2 className="text-2xl font-bold text-white" data-testid="text-empty-headline">
            Your competitors are posting — you're not
          </h2>
          <p className="text-white/50 text-sm max-w-md mx-auto">
            {competitorCount > 0
              ? `${competitorCount} competitor${competitorCount > 1 ? "s" : ""} posted an average of ${avgCompPosts.toFixed(0)} times this week. A full month of keyword-optimized posts keeps you visible in local search.`
              : "A full month of keyword-optimized posts keeps you visible in local search and ahead of competitors."}
          </p>
        </div>

        <div className="flex items-center justify-center gap-6 text-sm">
          <div className="flex items-center gap-2 text-white/40">
            <TrendingUp className="w-4 h-4 text-orange-400" />
            <span>Keyword-optimized</span>
          </div>
          <div className="flex items-center gap-2 text-white/40">
            <Zap className="w-4 h-4 text-orange-400" />
            <span>AI-generated</span>
          </div>
        </div>

        <Button
          size="lg"
          className="text-white font-bold text-base px-8 py-6 rounded-xl"
          style={{ background: DD.accent }}
          onClick={onGenerateMonth}
          disabled={isGenerating}
          data-testid="button-generate-month"
        >
          {isGenerating ? (
            <>Generating...</>
          ) : (
            <>
              <Zap className="w-5 h-5 mr-2" />
              Generate My Month
            </>
          )}
        </Button>

        <p className="text-white/20 text-xs">
          Creates ~20 keyword-optimized posts across your weak areas
        </p>
      </div>
    </div>
  );
}
